"use client";

import {
  ColumnDef,
  SortDirection,
  Table as TableType,
  flexRender,
} from "@tanstack/react-table";
import { forwardRef, useCallback, useEffect, useState } from "react";
import { TableVirtuoso } from "react-virtuoso";
import { cn } from "@/lib/utils";
import useAnalyticsManager from "@/hooks/miscellaneous/useAnalyticsManager";
import { ShimmerLoader } from "./ShimmerLoader";
import React from "react";

const Table = forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement>
>(({ className, ...props }, ref) => (
  <table
    ref={ref}
    className={cn("w-full caption-bottom text-sm", className)}
    {...props}
  />
));
Table.displayName = "Table";

const TableHeader = forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn("[&_tr]:border-b", className)} {...props} />
));
TableHeader.displayName = "TableHeader";

const TableBody = forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn("[&_tr:last-child]:border-0", className)}
    {...props}
  />
));
TableBody.displayName = "TableBody";

const TableRow = forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      "whitespace-nowrap border-t border-brand-border hover:bg-brand-surface data-[state=selected]:bg-brand-surface",
      className
    )}
    {...props}
  />
));
TableRow.displayName = "TableRow";

const TableHead = forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      "whitespace-nowrap px-4 py-3 text-left align-middle font-mono text-[11px] uppercase leading-5 tracking-[0.18em] text-brand-muted [&:has([role=checkbox])]:pr-0",
      className
    )}
    {...props}
  />
));
TableHead.displayName = "TableHead";

const TableCell = forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      "px-4 py-4 text-left align-middle text-[13px] [&:has([role=checkbox])]:pr-0",
      className
    )}
    {...props}
  />
));
TableCell.displayName = "TableCell";

// Sort indicator component
function SortingIndicator({ isSorted }: { isSorted: SortDirection | false }) {
  if (!isSorted) return null;
  return (
    <div className="ml-2">
      {
        {
          asc: "↑",
          desc: "↓",
        }[isSorted]
      }
    </div>
  );
}

interface VirtualTableProps<TData> {
  table: TableType<TData>;
  loadMore: () => Promise<void>;
  hasNextPage?: boolean;
  isLoading?: boolean;
  isFetchingNextPage?: boolean;
  columns: ColumnDef<TData>[];
  height?: string;
  overscan?: number;
  shimmerLoaderWidth?: number;
  shimmerLoaderHeight?: number;
  cellPadding?: string;
}

interface TableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  "data-index": number;
}

export function VirtualTable<TData>({
  table,
  loadMore,
  hasNextPage,
  isLoading,
  isFetchingNextPage,
  columns,
  height = "600px",
  overscan = 50,
  shimmerLoaderWidth = 160,
  shimmerLoaderHeight = 20,
  cellPadding = "px-4",
}: VirtualTableProps<TData>) {
  const { rows } = table.getRowModel();
  const { sendEvent } = useAnalyticsManager();
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const handleEndReached = useCallback(async () => {
    if (!hasNextPage || isLoadingMore) return;

    try {
      setIsLoadingMore(true);
      const lastVisibleRow = rows[rows.length - 1];
      sendEvent("job_list_scroll_to_bottom", {
        jobs_visible_count: rows.length,
        last_visible_job_id: lastVisibleRow?.id || 0,
      });
      await loadMore();
    } catch (error) {
      console.error("Error loading more data:", error);
      // You might want to show an error message to the user here
    } finally {
      setIsLoadingMore(false);
    }
  }, [hasNextPage, isLoadingMore, rows, loadMore, sendEvent]);

  // Validate table data structure
  useEffect(() => {
    if (!table || !table.getRowModel) {
      console.error("Invalid table data structure provided");
    }
  }, [table]);

  // Calculate the total count including shimmer rows
  const totalCount = isFetchingNextPage ? rows.length + 5 : rows.length;

  return (
    <div
      role="region"
      aria-label="Data table"
      aria-busy={isLoading}
      className="border border-brand-border"
    >
      <TableVirtuoso
        style={{ height }}
        totalCount={totalCount}
        endReached={hasNextPage ? handleEndReached : undefined}
        overscan={overscan}
        components={{
          Table: Table,
          TableRow: (props: TableRowProps) => {
            const index = props["data-index"];

            // If we're in shimmer rows territory
            if (isFetchingNextPage && index >= rows.length) {
              return (
                <TableRow
                  className="border-t border-brand-border text-left text-brand-text"
                  role="row"
                  aria-busy="true"
                  {...props}
                >
                  {columns.map((column, cellIndex) => (
                    <TableCell
                      key={column.id}
                      className={cn("cursor-pointer", {
                        [cellPadding]:
                          cellIndex === 0 || cellIndex === columns.length - 1,
                      })}
                      role="cell"
                    >
                      <ShimmerLoader
                        height={shimmerLoaderHeight}
                        width={shimmerLoaderWidth}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              );
            }

            // Regular row rendering
            const row = rows[index];
            if (!row) return null;

            return (
              <TableRow
                key={row.id}
                className="border-t border-brand-border text-left text-brand-text"
                role="row"
                {...props}
              >
                {row.getVisibleCells().map((cell, cellIndex) => (
                  <TableCell
                    key={cell.id}
                    className={cn("cursor-pointer", {
                      [cellPadding]:
                        cellIndex === 0 ||
                        cellIndex === row.getVisibleCells().length - 1,
                    })}
                    role="cell"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            );
          },
        }}
        fixedHeaderContent={() =>
          table.getHeaderGroups().map((headerGroup) => (
            <TableRow
              key={headerGroup.id}
              className="sticky top-0"
              role="row"
            >
              {headerGroup.headers.map((header, index) => (
                <TableHead
                  key={header.id}
                  className={cn(
                    "whitespace-nowrap bg-brand-surface px-4 py-3",
                    {
                      [cellPadding]:
                        index === 0 || index === headerGroup.headers.length - 1,
                    }
                  )}
                  style={{
                    width: header.getSize(),
                  }}
                  role="columnheader"
                  aria-sort={
                    header.column.getIsSorted()
                      ? header.column.getIsSorted() === "asc"
                        ? "ascending"
                        : "descending"
                      : "none"
                  }
                >
                  {header.isPlaceholder ? null : (
                    <div
                      className="flex items-center justify-start"
                      {...{
                        style: header.column.getCanSort()
                          ? {
                              cursor: "pointer",
                              userSelect: "none",
                            }
                          : {},
                        onClick: header.column.getToggleSortingHandler(),
                        role: header.column.getCanSort() ? "button" : undefined,
                        "aria-label": `Sort by ${flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}`,
                      }}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      <SortingIndicator
                        isSorted={header.column.getIsSorted()}
                      />
                    </div>
                  )}
                </TableHead>
              ))}
            </TableRow>
          ))
        }
      />

      {!isLoading && rows.length === 0 && (
        <div
          className="p-4 text-center text-shades-neutral-300"
          role="status"
          aria-live="polite"
        >
          No results found
        </div>
      )}
    </div>
  );
}
